import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'
import { useKeyboardShortcuts } from '../contexts/KeyboardShortcutsContext'

interface AccessibilitySettings {
  highContrast: boolean
  reducedMotion: boolean
  largeText: boolean
  screenReaderMode: boolean
  focusIndicators: boolean
  keyboardNavigation: boolean
  colorBlindMode: string
  textSpacing: string
  cursorSize: string
  audioDescriptions: boolean
  autoplayMedia: boolean
}

interface Shortcut {
  id: string
  key: string
  description: string
  category: string
  enabled: boolean
}

interface ColorBlindMode {
  id: string
  name: string
  description: string
}

const API_BASE = '/api/accessibility'

export default function AccessibilitySettingsPage() {
  const { shortcuts, openHelp } = useKeyboardShortcuts()
  const [settings, setSettings] = useState<AccessibilitySettings | null>(null)
  const [colorBlindModes, setColorBlindModes] = useState<ColorBlindMode[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'settings' | 'shortcuts' | 'report'>('settings')

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      const [settingsRes, modesRes] = await Promise.all([
        fetch(`${API_BASE}/settings`),
        fetch(`${API_BASE}/color-blind-modes`),
      ])

      if (settingsRes.ok) {
        setSettings(await settingsRes.json())
      }
      if (modesRes.ok) {
        setColorBlindModes(await modesRes.json())
      }
    } catch (err) {
      console.error('Failed to load data:', err)
    } finally {
      setLoading(false)
    }
  }

  const updateSetting = async (key: keyof AccessibilitySettings, value: boolean | string) => {
    if (!settings) return

    const newSettings = { ...settings, [key]: value }
    setSettings(newSettings)

    try {
      await fetch(`${API_BASE}/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          updates: { [key]: value },
          user: 'default_user',
        }),
      })

      // Apply settings to document
      applySettings(newSettings)
    } catch (err) {
      console.error('Failed to update setting:', err)
    }
  }

  const applySettings = (s: AccessibilitySettings) => {
    const root = document.documentElement

    // High contrast
    if (s.highContrast) {
      root.classList.add('high-contrast')
    } else {
      root.classList.remove('high-contrast')
    }

    // Reduced motion
    if (s.reducedMotion) {
      root.classList.add('reduced-motion')
    } else {
      root.classList.remove('reduced-motion')
    }

    // Large text
    if (s.largeText) {
      root.style.fontSize = '18px'
    } else {
      root.style.fontSize = ''
    }

    // Text spacing
    if (s.textSpacing === 'increased') {
      root.style.letterSpacing = '0.05em'
      root.style.wordSpacing = '0.1em'
      root.style.lineHeight = '1.8'
    } else {
      root.style.letterSpacing = ''
      root.style.wordSpacing = ''
      root.style.lineHeight = ''
    }
  }

  const resetSettings = async () => {
    try {
      const response = await fetch(`${API_BASE}/settings/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user: 'default_user' }),
      })

      if (response.ok) {
        const newSettings = await response.json()
        setSettings(newSettings)
        applySettings(newSettings)
      }
    } catch (err) {
      console.error('Failed to reset settings:', err)
    }
  }

  const groupedShortcuts = shortcuts.reduce((acc, shortcut) => {
    if (!acc[shortcut.category]) {
      acc[shortcut.category] = []
    }
    acc[shortcut.category].push(shortcut)
    return acc
  }, {} as Record<string, Shortcut[]>)

  const categoryNames: Record<string, string> = {
    navigation: 'Navigation',
    actions: 'Actions',
    interface: 'Interface',
    data: 'Data Navigation',
  }

  const formatKey = (key: string) => {
    return key
      .split('+')
      .map((k) => {
        if (k === 'ctrl') return 'Ctrl'
        if (k === 'shift') return 'Shift'
        if (k === 'alt') return 'Alt'
        if (k === 'cmd') return 'Cmd'
        if (k === 'esc') return 'Esc'
        if (k === 'enter') return 'Enter'
        if (k === 'del') return 'Delete'
        if (k === 'tab') return 'Tab'
        return k.toUpperCase()
      })
      .join(' + ')
  }

  const renderSettings = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h3 style={{ margin: 0, color: '#1F2937' }}>Accessibility Settings</h3>
        <button
          onClick={resetSettings}
          style={{
            padding: '8px 16px',
            backgroundColor: '#6B7280',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
          }}
        >
          Reset to Defaults
        </button>
      </div>

      {loading ? (
        <p>Loading settings...</p>
      ) : !settings ? (
        <p>Failed to load settings</p>
      ) : (
        <div style={{ display: 'grid', gap: '24px' }}>
          {/* Visual Settings */}
          <div style={{ backgroundColor: '#F9FAFB', padding: '20px', borderRadius: '8px' }}>
            <h4 style={{ margin: '0 0 16px 0', color: '#374151' }}>Visual Settings</h4>
            <div style={{ display: 'grid', gap: '16px' }}>
              <SettingToggle
                label="High Contrast Mode"
                description="Increase contrast for better visibility"
                checked={settings.highContrast}
                onChange={(v) => updateSetting('highContrast', v)}
              />
              <SettingToggle
                label="Large Text"
                description="Increase base font size throughout the app"
                checked={settings.largeText}
                onChange={(v) => updateSetting('largeText', v)}
              />
              <SettingToggle
                label="Reduced Motion"
                description="Minimize animations and transitions"
                checked={settings.reducedMotion}
                onChange={(v) => updateSetting('reducedMotion', v)}
              />
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500, color: '#374151' }}>
                  Color Blind Mode
                </label>
                <select
                  value={settings.colorBlindMode}
                  onChange={(e) => updateSetting('colorBlindMode', e.target.value)}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '14px',
                    minWidth: '200px',
                  }}
                >
                  {colorBlindModes.map((mode) => (
                    <option key={mode.id} value={mode.id}>
                      {mode.name} - {mode.description}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500, color: '#374151' }}>
                  Text Spacing
                </label>
                <select
                  value={settings.textSpacing}
                  onChange={(e) => updateSetting('textSpacing', e.target.value)}
                  style={{
                    padding: '8px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '14px',
                    minWidth: '200px',
                  }}
                >
                  <option value="normal">Normal</option>
                  <option value="increased">Increased (WCAG 1.4.12 compliant)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Navigation Settings */}
          <div style={{ backgroundColor: '#F9FAFB', padding: '20px', borderRadius: '8px' }}>
            <h4 style={{ margin: '0 0 16px 0', color: '#374151' }}>Navigation & Interaction</h4>
            <div style={{ display: 'grid', gap: '16px' }}>
              <SettingToggle
                label="Keyboard Navigation"
                description="Enable keyboard shortcuts for navigation"
                checked={settings.keyboardNavigation}
                onChange={(v) => updateSetting('keyboardNavigation', v)}
              />
              <SettingToggle
                label="Focus Indicators"
                description="Show visible focus indicators when navigating with keyboard"
                checked={settings.focusIndicators}
                onChange={(v) => updateSetting('focusIndicators', v)}
              />
              <SettingToggle
                label="Screen Reader Mode"
                description="Optimize interface for screen readers"
                checked={settings.screenReaderMode}
                onChange={(v) => updateSetting('screenReaderMode', v)}
              />
            </div>
          </div>

          {/* Media Settings */}
          <div style={{ backgroundColor: '#F9FAFB', padding: '20px', borderRadius: '8px' }}>
            <h4 style={{ margin: '0 0 16px 0', color: '#374151' }}>Media Settings</h4>
            <div style={{ display: 'grid', gap: '16px' }}>
              <SettingToggle
                label="Autoplay Media"
                description="Automatically play videos and animations"
                checked={settings.autoplayMedia}
                onChange={(v) => updateSetting('autoplayMedia', v)}
              />
              <SettingToggle
                label="Audio Descriptions"
                description="Enable audio descriptions for visual content"
                checked={settings.audioDescriptions}
                onChange={(v) => updateSetting('audioDescriptions', v)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )

  const renderShortcuts = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h3 style={{ margin: 0, color: '#1F2937' }}>Keyboard Shortcuts</h3>
        <button
          onClick={openHelp}
          style={{
            padding: '8px 16px',
            backgroundColor: '#4A90E2',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
          }}
        >
          Show Quick Reference
        </button>
      </div>

      <div style={{ display: 'grid', gap: '24px' }}>
        {Object.entries(groupedShortcuts).map(([category, categoryShortcuts]) => (
          <div key={category} style={{ backgroundColor: '#F9FAFB', padding: '20px', borderRadius: '8px' }}>
            <h4 style={{ margin: '0 0 16px 0', color: '#374151' }}>{categoryNames[category] || category}</h4>
            <div style={{ display: 'grid', gap: '8px' }}>
              {categoryShortcuts.map((shortcut) => (
                <div
                  key={shortcut.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px',
                    backgroundColor: 'white',
                    borderRadius: '6px',
                    border: '1px solid #E5E7EB',
                    opacity: shortcut.enabled ? 1 : 0.5,
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 500, color: '#1F2937', marginBottom: '2px' }}>
                      {shortcut.description}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6B7280' }}>{shortcut.id}</div>
                  </div>
                  <kbd
                    style={{
                      padding: '6px 10px',
                      backgroundColor: '#F3F4F6',
                      border: '1px solid #D1D5DB',
                      borderRadius: '4px',
                      fontSize: '13px',
                      fontFamily: 'monospace',
                      color: '#1F2937',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                    }}
                  >
                    {formatKey(shortcut.key)}
                  </kbd>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          marginTop: '24px',
          padding: '16px',
          backgroundColor: '#EBF5FF',
          borderRadius: '8px',
          border: '1px solid #4A90E2',
        }}
      >
        <div style={{ fontWeight: 600, color: '#1E40AF', marginBottom: '8px' }}>Quick Tip</div>
        <div style={{ color: '#1E40AF', fontSize: '14px' }}>
          Press <kbd style={{ padding: '2px 6px', backgroundColor: 'white', borderRadius: '3px' }}>?</kbd> at any
          time to open the keyboard shortcuts quick reference modal.
        </div>
      </div>
    </div>
  )

  const renderReport = () => (
    <div>
      <h3 style={{ margin: '0 0 24px 0', color: '#1F2937' }}>Accessibility Report</h3>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
          <div style={{ fontSize: '28px', fontWeight: 700, color: '#10B981' }}>WCAG 2.1</div>
          <div style={{ color: '#6B7280', fontSize: '14px' }}>Target Level: AA</div>
        </div>
        <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
          <div style={{ fontSize: '28px', fontWeight: 700, color: '#4A90E2' }}>
            {shortcuts.filter((s) => s.enabled).length}
          </div>
          <div style={{ color: '#6B7280', fontSize: '14px' }}>Active Shortcuts</div>
        </div>
        <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
          <div style={{ fontSize: '28px', fontWeight: 700, color: '#7B68EE' }}>8</div>
          <div style={{ color: '#6B7280', fontSize: '14px' }}>Supported Languages</div>
        </div>
      </div>

      <div style={{ backgroundColor: '#F9FAFB', padding: '20px', borderRadius: '8px', marginBottom: '24px' }}>
        <h4 style={{ margin: '0 0 16px 0', color: '#374151' }}>Accessibility Features</h4>
        <div style={{ display: 'grid', gap: '12px' }}>
          <FeatureItem icon="⌨️" name="Full Keyboard Navigation" status="Supported" />
          <FeatureItem icon="🔊" name="Screen Reader Compatible" status="Supported" />
          <FeatureItem icon="🎨" name="High Contrast Mode" status="Supported" />
          <FeatureItem icon="👁️" name="Color Blind Modes" status="Supported" />
          <FeatureItem icon="📝" name="Text Scaling" status="Supported" />
          <FeatureItem icon="🎬" name="Reduced Motion" status="Supported" />
          <FeatureItem icon="🔤" name="ARIA Labels" status="Implemented" />
          <FeatureItem icon="🖱️" name="Focus Management" status="Implemented" />
        </div>
      </div>

      <div style={{ backgroundColor: '#F9FAFB', padding: '20px', borderRadius: '8px' }}>
        <h4 style={{ margin: '0 0 16px 0', color: '#374151' }}>WCAG Compliance Checklist</h4>
        <div style={{ display: 'grid', gap: '8px' }}>
          <ChecklistItem text="1.1.1 Non-text Content - Alt text for images" checked />
          <ChecklistItem text="1.3.1 Info and Relationships - Semantic HTML" checked />
          <ChecklistItem text="1.4.1 Use of Color - Not sole conveyor of info" checked />
          <ChecklistItem text="1.4.3 Contrast (Minimum) - 4.5:1 ratio" checked />
          <ChecklistItem text="2.1.1 Keyboard - All functionality via keyboard" checked />
          <ChecklistItem text="2.4.3 Focus Order - Logical navigation sequence" checked />
          <ChecklistItem text="2.4.7 Focus Visible - Visible focus indicator" checked />
          <ChecklistItem text="3.2.1 On Focus - No unexpected context changes" checked />
          <ChecklistItem text="4.1.2 Name, Role, Value - ARIA attributes" checked />
        </div>
      </div>
    </div>
  )

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#F9FAFB' }}>
      <Header />

      <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ margin: '0 0 8px 0', fontSize: '24px', color: '#1F2937' }}>Accessibility & Shortcuts</h1>
          <p style={{ margin: 0, color: '#6B7280' }}>
            Configure accessibility features and keyboard shortcuts
          </p>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: '1px solid #E5E7EB', paddingBottom: '12px' }}>
          {[
            { id: 'settings' as const, label: 'Settings' },
            { id: 'shortcuts' as const, label: 'Keyboard Shortcuts' },
            { id: 'report' as const, label: 'Compliance Report' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '8px 16px',
                backgroundColor: activeTab === tab.id ? '#4A90E2' : 'transparent',
                color: activeTab === tab.id ? 'white' : '#6B7280',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 500,
                fontSize: '14px',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', border: '1px solid #E5E7EB' }}>
          {activeTab === 'settings' && renderSettings()}
          {activeTab === 'shortcuts' && renderShortcuts()}
          {activeTab === 'report' && renderReport()}
        </div>
      </div>
    </div>
  )
}

// Toggle component
const SettingToggle: React.FC<{
  label: string
  description: string
  checked: boolean
  onChange: (value: boolean) => void
}> = ({ label, description, checked, onChange }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <div>
      <div style={{ fontWeight: 500, color: '#374151' }}>{label}</div>
      <div style={{ fontSize: '13px', color: '#6B7280' }}>{description}</div>
    </div>
    <button
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      style={{
        width: '48px',
        height: '24px',
        borderRadius: '12px',
        backgroundColor: checked ? '#4A90E2' : '#D1D5DB',
        border: 'none',
        cursor: 'pointer',
        position: 'relative',
        transition: 'background-color 0.2s',
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: '2px',
          left: checked ? '26px' : '2px',
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          backgroundColor: 'white',
          transition: 'left 0.2s',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        }}
      />
    </button>
  </div>
)

// Feature item component
const FeatureItem: React.FC<{ icon: string; name: string; status: string }> = ({ icon, name, status }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
    <span style={{ fontSize: '20px' }}>{icon}</span>
    <span style={{ flex: 1, color: '#374151' }}>{name}</span>
    <span
      style={{
        padding: '4px 8px',
        backgroundColor: '#D1FAE5',
        color: '#065F46',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: 500,
      }}
    >
      {status}
    </span>
  </div>
)

// Checklist item component
const ChecklistItem: React.FC<{ text: string; checked: boolean }> = ({ text, checked }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
    <span style={{ color: checked ? '#10B981' : '#9CA3AF', fontSize: '18px' }}>{checked ? '✓' : '○'}</span>
    <span style={{ color: checked ? '#374151' : '#9CA3AF', fontSize: '14px' }}>{text}</span>
  </div>
)
